if (!window.Eurus.loadedScript.has('product-sibling.js')) {
  window.Eurus.loadedScript.add('product-sibling.js');

  requestAnimationFrame(() => {
    document.addEventListener("alpine:init", () => {
      Alpine.data("xProductSibling", (sectionId, isProductPage, redirect, config) => ({
        cachedResults: [],
        cfg: config || {},
        siblings: [],
        selectedLabel: '',
        siblingsMarkup: '',

        init() {
          if (this.cfg.feedUrl) this._loadSiblings();
        },

        /* ------------------------------------------------------------------
         * Tag mode: fetch the sibling feed and build the swatch markup.
         * Liquid cannot query products by tag, so the list is resolved through
         * /collections/all/<tag-handle>?view=sibling-data (see
         * templates/collection.sibling-data.liquid).
         * ---------------------------------------------------------------- */
        async _loadSiblings() {
          const url = this.cfg.feedUrl;
          const cacheKey = 'eurus:siblings:' + url;

          const cached = this._readCache(cacheKey);
          if (cached) {
            this._applySiblings(cached);
            return;
          }

          try {
            const response = await fetch(url);
            if (!response.ok) return;
            const data = await response.json();
            if (!Array.isArray(data)) return;
            this._writeCache(cacheKey, data);
            this._applySiblings(data);
          } catch (e) {
            /* Feed unavailable - leave the block empty rather than breaking the page. */
          }
        },

        _applySiblings(data) {
          this.siblings = data;
          const current = data.find((item) => item.id === this.cfg.currentId);
          this.selectedLabel = current ? current.label : '';
          this.siblingsMarkup =
            this.cfg.variantType === 'dropdown' ? this._buildSelect() : this._buildSwatches();
        },

        _readCache(key) {
          try {
            const raw = sessionStorage.getItem(key);
            return raw ? JSON.parse(raw) : null;
          } catch (e) {
            return null;
          }
        },

        _writeCache(key, data) {
          try {
            sessionStorage.setItem(key, JSON.stringify(data));
          } catch (e) {
            /* Quota or private mode - caching is optional. */
          }
        },

        _buildSwatches() {
          const cfg = this.cfg;
          const name = `${cfg.blockId}-${cfg.optionName}`;

          return this.siblings
            .map((item, index) => {
              const inputId = `${cfg.blockId}-tag-${index}`;
              const label = this._esc(item.label || item.title || '');
              const checked = item.id === cfg.currentId ? ' checked' : '';

              const input =
                `<input type="radio" id="${inputId}" name="${this._esc(name)}"` +
                ` value="${label}" class="absolute opacity-0 h-1 w-1 input-radio"` +
                ` data-product-url="${this._esc(item.url)}"${checked}>`;

              if (!cfg.swatchEnabled) {
                return (
                  input +
                  `<label tabindex="0" class="${this._esc(cfg.textClass)}" for="${inputId}">` +
                  `<span>${label}</span></label>`
                );
              }

              let style = '';
              if (item.image) {
                // `.color-watches` already sets background-size/position, so painting the
                // label directly keeps this identical to the server rendered variant.
                style = `background-image:url(${item.image});`;
                if (cfg.swatchNatural && item.ratio) style += `--ratio-image:${item.ratio}%;`;
              } else if (item.swatch) {
                style = `background:${this._esc(item.swatch)};`;
              }

              const natural =
                cfg.swatchNatural && item.image ? ' color-swatch-natural' : '';

              return (
                input +
                `<label id="color-swatch-${inputId}" class="${this._esc(cfg.swatchClass)}${natural}"` +
                ` for="${inputId}" tabindex="0" aria-label="${label}" title="${label}"` +
                ` style="${style}"></label>`
              );
            })
            .join('');
        },

        _buildSelect() {
          const cfg = this.cfg;
          const options = this.siblings
            .map((item) => {
              const label = this._esc(item.label || item.title || '');
              const selected = item.id === cfg.currentId ? ' selected' : '';
              return `<option value="${label}" data-product-url="${this._esc(item.url)}"${selected}>${label}</option>`;
            })
            .join('');

          return (
            `<select class="${this._esc(cfg.selectClass)}" name="${this._esc(cfg.blockId + '-' + cfg.optionName)}">` +
            options +
            `</select>`
          );
        },

        /* Delegated handlers - markup injected via x-html is not scanned by Alpine. */
        onSiblingChange(event) {
          const target = event.target;
          if (!target) return;

          if (target.tagName === 'SELECT') {
            const option = target.selectedOptions[0];
            if (option) this.updateProductInfo(option.dataset.productUrl);
            return;
          }

          const url = target.dataset && target.dataset.productUrl;
          if (url) this.updateProductInfo(url);
        },

        onSiblingEnter(event) {
          const label = event.target.closest('label[for]');
          if (!label) return;
          const input = document.getElementById(label.getAttribute('for'));
          if (input && input.dataset.productUrl) this.updateProductInfo(input.dataset.productUrl);
        },

        _esc(value) {
          return String(value == null ? '' : value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
        },

        /* ------------------------------------------------------------------
         * Shared navigation / AJAX swap (all sibling sources).
         * ---------------------------------------------------------------- */
        updateProductInfo(url) {
          if (!url) return;
          if (redirect) {
            window.location.href = url;
            return
          }
          const link = isProductPage?`${url}`:`${url}?section_id=${sectionId}`;

          if (this.cachedResults[link]) {
            const html = this.cachedResults[link];
            this._handleSwapProduct(html);
          } else {
            fetch(link)
            .then((response) => response.text())
            .then((responseText) => {
              const html = new DOMParser().parseFromString(responseText, 'text/html');
              this._updateTitle(html);
              this._handleSwapProduct(html);
              this._updateFbtContainer(html)
              this.cachedResults[link] = html;
            })
          }
          this._updateURL(url);
        },
        changeSelectOption(event) {
          const input = event.target.selectedOptions[0];
          const targetUrl = input.dataset.productUrl;
          this.updateProductInfo(targetUrl);
        },
        _updateURL(url) {
          if (!isProductPage) return;
          window.history.replaceState({}, '', `${url}`);
        },
        _updateTitle(html) {
          if (!isProductPage) return;
          document.querySelector('head title').textContent = html.querySelector('.product-title').textContent;
          const destination = document.querySelector('#breadcrumbs--' + sectionId);
          const source = html.querySelector('#breadcrumbs--' + sectionId);
          if (source && destination) destination.innerHTML = source.innerHTML;
        },
        _updateFbtContainer(html) {
          if (!isProductPage) return;
          const destination = document.querySelector('#popup-fbt-' + sectionId);
          const source = html.querySelector('#popup-fbt-' + sectionId);
          if (source && destination) destination.outerHTML = source.outerHTML;
        },
        _handleSwapProduct(html) {
          const destination = isProductPage ? document.querySelector('.main-product'):document.querySelector('.x-product-' + sectionId);
          const source = isProductPage ? html.querySelector('.main-product') : html.querySelector('.x-product-' + sectionId);
          if (source && destination) destination.innerHTML = source.innerHTML;
        }
      }));
    });
  });
}
